import { useEffect, useState } from 'react';
import { DEFAULT_BIG2_POINT_LIMIT, type GameType, type RoomMode } from '@big2/shared';
import { useI18n } from '../../i18n.js';
import './matchmaking.css';

export interface MatchmakingBannerProps {
  status: 'searching' | 'found' | 'timeout';
  gameType: GameType;
  mode: RoomMode;
  elapsedSeconds: number;
  estimatedSeconds: number;
  timeoutSeconds?: number;
  playersInQueue?: number;
  matchingRoomsCount?: number;
  onCancel: () => void;
  onCreateRoom?: () => void;
  onRetrySearch?: () => void;
}

export function formatQueueClock(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const mins = Math.floor(clamped / 60);
  const secs = clamped % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function getGameDisplayTitle(gameType: GameType, isMn: boolean): string {
  if (gameType === 'daaluu') return isMn ? '🂍 Даалуу' : '🂍 Daaluu';
  if (gameType === 'twentyone') return isMn ? '♦ 21 (Очко)' : '♦ 21 (Ochko)';
  if (gameType === 'muushig') return isMn ? '♣ Муушиг' : '♣ Muushig';
  return isMn ? `♠ Арван гурав (${DEFAULT_BIG2_POINT_LIMIT} оноо)` : `♠ Big Two (${DEFAULT_BIG2_POINT_LIMIT} pts)`;
}

export function MatchmakingBanner({
  status,
  gameType,
  mode,
  elapsedSeconds,
  estimatedSeconds,
  timeoutSeconds = 120,
  playersInQueue = 1,
  matchingRoomsCount = 0,
  onCancel,
  onCreateRoom,
  onRetrySearch,
}: MatchmakingBannerProps) {
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const progressPct =
    status === 'found' || status === 'timeout'
      ? 100
      : Math.min(98, Math.round((elapsedSeconds / Math.max(1, timeoutSeconds)) * 100));
  const gameTitle = getGameDisplayTitle(gameType, isMn);
  const modeLabel = mode === 'tsai' ? (isMn ? '🍵 Цайтай (Өрсөлдөөнт)' : '🍵 Tsai (Competitive)') : (isMn ? 'Чөлөөт (Энгийн)' : 'Free (Casual)');

  return (
    <div
      className={`matchmaking-dock-banner${status === 'found' ? ' is-found' : ''}${status === 'timeout' ? ' is-timeout' : ''}`}
      role="status"
      aria-live="polite"
      data-testid="matchmaking-banner"
    >
      <div className="matchmaking-dock-inner">
        <div className="matchmaking-radar-wrap" aria-hidden="true">
          <span
            className={`matchmaking-radar-dot${status === 'found' ? ' is-found' : ''}${status === 'timeout' ? ' is-timeout' : ''}`}
          />
          {status === 'searching' && <span className="matchmaking-radar-ring" />}
        </div>

        <div className="matchmaking-info">
          <div className="matchmaking-headline">
            {status === 'found' ? (
              <span className="matchmaking-found-text">
                {isMn ? '✨ ТОГЛОЛТ ОЛДЛОО! Ширээ рүү орж байна...' : '✨ MATCH FOUND! Joining table...'}
              </span>
            ) : status === 'timeout' ? (
              <span className="matchmaking-timeout-text">
                {isMn
                  ? '⚠️ ОДООГООР НЭЭЛТТЭЙ ӨРӨӨ ОЛДСОНГҮЙ'
                  : '⚠️ NO ACTIVE PUBLIC ROOMS FOUND'}
              </span>
            ) : (
              <span>
                {isMn ? 'ОНЛАЙН ТОГЛОЛТ ХАЙЖ БАЙНА...' : 'SEARCHING FOR PUBLIC MATCH...'}
              </span>
            )}
          </div>
          <div className="matchmaking-meta">
            <span>{gameTitle}</span>
            <span className="matchmaking-sep">·</span>
            <span>{modeLabel}</span>
            {status === 'timeout' ? (
              <>
                <span className="matchmaking-sep">·</span>
                <span>
                  {isMn
                    ? 'Та өөрийн өрөөг үүсгэж шууд тоглох эсвэл дахин хайх боломжтой.'
                    : 'You can create your own room now or search again.'}
                </span>
              </>
            ) : (
              <>
                {playersInQueue > 1 && (
                  <>
                    <span className="matchmaking-sep">·</span>
                    <span className="matchmaking-active-highlight">
                      {isMn ? `${playersInQueue} тоглогч хайж байна` : `${playersInQueue} players searching`}
                    </span>
                  </>
                )}
                {matchingRoomsCount > 0 && status === 'searching' && (
                  <>
                    <span className="matchmaking-sep">·</span>
                    <span className="matchmaking-active-highlight">
                      {isMn ? `${matchingRoomsCount} нээлттэй ширээ` : `${matchingRoomsCount} open table(s)`}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="matchmaking-timers">
          <div className="matchmaking-clock" title={isMn ? 'Хайсан хугацаа' : 'Elapsed time'}>
            <span className="matchmaking-clock-label">{isMn ? 'Хугацаа' : 'Elapsed'}</span>
            <strong className="matchmaking-clock-value" data-testid="matchmaking-elapsed">
              ⏱️ {formatQueueClock(elapsedSeconds)}
            </strong>
          </div>
          {status === 'searching' && (
            <div className="matchmaking-estimate" title={isMn ? 'Тооцоолсон хүлээх хугацаа' : 'Dynamic estimated wait'}>
              <span className="matchmaking-clock-label">{isMn ? 'Тооцоолол' : 'Est. Wait'}</span>
              <span className="matchmaking-estimate-value" data-testid="matchmaking-estimated">
                ~{formatQueueClock(estimatedSeconds)}
              </span>
            </div>
          )}
        </div>

        {status === 'searching' && (
          <button
            type="button"
            className="matchmaking-cancel-btn"
            onClick={onCancel}
            aria-label={isMn ? 'Хайлт цуцлах' : 'Cancel matchmaking'}
          >
            ✕ {isMn ? 'Цуцлах' : 'Cancel'}
          </button>
        )}

        {status === 'timeout' && (
          <div className="matchmaking-timeout-actions">
            {onCreateRoom && (
              <button
                type="button"
                className="lobby-mm-start-now-btn"
                onClick={onCreateRoom}
              >
                🏠 {isMn ? 'Өрөө үүсгэх' : 'Create Room'}
              </button>
            )}
            {onRetrySearch && (
              <button
                type="button"
                className="lobby-mm-hold-btn"
                onClick={onRetrySearch}
              >
                🔄 {isMn ? 'Дахин хайх' : 'Search Again'}
              </button>
            )}
            <button
              type="button"
              className="matchmaking-cancel-btn"
              onClick={onCancel}
              aria-label={isMn ? 'Хаах' : 'Dismiss'}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className="matchmaking-progress-track" aria-hidden="true">
        <div className="matchmaking-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>
    </div>
  );
}

export interface LobbyMatchmakingBarProps {
  humanCount: number;
  totalSeats: number;
  fillWithBots: boolean;
  isHost: boolean;
  canStart: boolean;
  onStartNow: () => void;
}

export function LobbyMatchmakingBar({
  humanCount,
  totalSeats,
  fillWithBots,
  isHost,
  canStart,
  onStartNow,
}: LobbyMatchmakingBarProps) {
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const [elapsedSec, setElapsedSec] = useState(0);

  const isTableFull = humanCount >= totalSeats;

  useEffect(() => {
    const iv = setInterval(() => {
      setElapsedSec((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div
      className={`lobby-matchmaking-bar${isTableFull ? ' is-ready' : ''}`}
      role="status"
      aria-live="polite"
      data-testid="lobby-matchmaking-bar"
    >
      <div className="lobby-matchmaking-top">
        <div className="lobby-matchmaking-status">
          <span className={`matchmaking-radar-dot${isTableFull ? ' is-found' : ''}`} aria-hidden="true" />
          <div className="lobby-matchmaking-text">
            <strong>
              {isTableFull
                ? isMn
                  ? `🎉 Ширээ дүүрлээ! (${humanCount}/${totalSeats} тоглогч)`
                  : `🎉 Table Full! (${humanCount}/${totalSeats} players)`
                : isMn
                ? `⚡ ШУУРХАЙ ТОГЛОЛТ — ТОГЛОГЧ ХАЙЖ БАЙНА (${humanCount}/${totalSeats})`
                : `⚡ QUICK MATCH — SEARCHING FOR PLAYERS (${humanCount}/${totalSeats})`}
            </strong>
            <span>
              {isTableFull
                ? isMn
                  ? 'Бүх суудал эзлэгдлээ — тоглолт эхлэхэд бэлэн.'
                  : 'All seats occupied by online players — ready to start.'
                : fillWithBots
                ? isMn
                  ? 'Бусад тоглогчид "Шуурхай тоглох"-оор орж ирнэ, эсвэл та ботуудтай шууд эхлүүлж болно.'
                  : 'Other players can join via Quick Play, or you can start now with bots.'
                : isMn
                ? 'Бусад тоглогчид "Шуурхай тоглох"-оор энэ ширээнд автоматаар холбогдоно.'
                : 'Other players clicking Quick Play will automatically join this table.'}
            </span>
          </div>
        </div>

        <div className="lobby-matchmaking-right">
          <div className="lobby-matchmaking-clocks">
            <span className="lobby-mm-clock" data-testid="lobby-queue-elapsed">
              ⏱️ {formatQueueClock(elapsedSec)}
            </span>
          </div>

          {isHost && canStart && (
            <div className="lobby-matchmaking-actions">
              <button
                type="button"
                className="lobby-mm-start-now-btn"
                onClick={onStartNow}
              >
                ⚡ {isMn ? 'Шууд эхлэх' : 'Start Now'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
